import { useState, useRef, useCallback, useImperativeHandle, forwardRef, useEffect, useMemo, memo } from 'react';
import { Map as GoogleMap, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import './BaseMap.scss';

const BaseMapContent = memo(forwardRef((props, ref) => {
    const [listingWindow, setListingWindow] = useState({
        location: null,
        mode: 'detail',
        locationsAtPoint: []
    });
    const [flightWindow, setFlightWindow] = useState({
        path: null,
        pathIndex: null,
        mode: 'detail',
        pathsAtPoint: [],
        filter: 'departure'
    });
    const map = useMap();
    const flightListScrollRef = useRef(null);

    const listingByIdRef = useRef(new Map());
    useEffect(() => {
        if (props.listingMapData && Array.isArray(props.listingMapData)) {
            listingByIdRef.current = new Map(props.listingMapData.map(l => [Number(l.Id), l]));
        }
    }, [props.listingMapData]);

    const setMapCenter = useCallback((lat, lng, distance) => {
        if (map) {
            if (props.isSearchDrivenUpdate) props.isSearchDrivenUpdate.current = true;
            map.setCenter({ lat, lng });

            if (distance === 0) {
                map.setZoom(2);
            } else if (distance < 25) {
                map.setZoom(11);
            } else if (distance < 51) {
                map.setZoom(9);
            } else if (distance < 151) {
                map.setZoom(7);
            } else {
                map.setZoom(6);
            }
        }
    }, [map]);

    useImperativeHandle(ref, () => ({
        setMapCenter
    }));

    const hasSetInitialZoomRef = useRef(false);
    useEffect(() => {
        if (!map || hasSetInitialZoomRef.current) return;
        hasSetInitialZoomRef.current = true;
        if (!props.defaultZoom) {
            map.setZoom(2);
        }
    }, [map]);

    const hasCenteredRef = useRef(false);
    const suppressNextIdleSearchRef = useRef(false);
    useEffect(() => {
        if (!map || hasCenteredRef.current || props.disableAutoCentering || props.defaultCenter) return;
        hasCenteredRef.current = true;

        const centerMap = (lat, lng) => {
            if (props.isSearchDrivenUpdate) props.isSearchDrivenUpdate.current = true;
            map.setCenter({ lat, lng });
        };

        const userLat = props.currentUser?.CurrentLocation?.Coordinates?.Latitude;
        const userLng = props.currentUser?.CurrentLocation?.Coordinates?.Longitude;
        if (userLat && userLng) {
            centerMap(userLat, userLng);
            return;
        }

        if (props.enableGeolocation !== false && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => centerMap(pos.coords.latitude, pos.coords.longitude),
                () => {},
                { timeout: 5000, maximumAge: 300000 }
            );
        }
    }, [map]);

    useEffect(() => {
        if (!map || !props.onLocationChange) return;

        let firstIdle = true;
        let debounceTimer = null;

        const idleListener = map.addListener('idle', () => {
            const isFirst = firstIdle;
            firstIdle = false;

            if (props.isSearchDrivenUpdate?.current) {
                props.isSearchDrivenUpdate.current = false;
                if (!isFirst) return;
            }

            if (isFirst) {
                return;
            }
            if (suppressNextIdleSearchRef.current) {
                suppressNextIdleSearchRef.current = false;
                clearTimeout(debounceTimer);
                return;
            }
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const bounds = map.getBounds();
                if (!bounds) return;
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                props.onLocationChange(expandBounds({
                    north: ne.lat(),
                    south: sw.lat(),
                    east: ne.lng(),
                    west: sw.lng()
                }));
            }, 400);
        });

        return () => {
            google.maps.event.removeListener(idleListener);
            clearTimeout(debounceTimer);
        };
    }, [map, props.onLocationChange]);

    const MARKER_ICON = useMemo(() => ({ path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z', fillColor: '#0066b3', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 1.5, anchor: new google.maps.Point(12, 22) }), []);

    const roundCoord = useCallback((val) => Math.round(val * 10000) / 10000, []);

    const formatPrice = useCallback((price) => {
        if (!price || price.trim() === '') {
            return null;
        }
        return price;
    }, []);

    const groupedLocations = useMemo(() => {
        if (!props.locations) return new Map();

        const groups = new Map();
        props.locations.forEach((loc, idx) => {
            const key = `${roundCoord(loc.Latitude)},${roundCoord(loc.Longitude)}`;

            if (!groups.has(key)) {
                groups.set(key, {
                    coordinate: { lat: loc.Latitude, lng: loc.Longitude },
                    locations: []
                });
            }
            groups.get(key).locations.push({ ...loc, index: idx });
        });

        return groups;
    }, [props.locations, roundCoord]);

    const groupedFlightPaths = useMemo(() => {
        if (!props.flightPaths) return new Map();

        const groups = new Map();
        props.flightPaths.forEach((path, idx) => {
            const fromKey = `${roundCoord(path.From.Latitude)},${roundCoord(path.From.Longitude)}`;
            if (!groups.has(fromKey)) {
                groups.set(fromKey, {
                    coordinate: { lat: path.From.Latitude, lng: path.From.Longitude },
                    paths: []
                });
            }
            groups.get(fromKey).paths.push({ ...path, index: idx, endpoint: 'from' });

            const toKey = `${roundCoord(path.To.Latitude)},${roundCoord(path.To.Longitude)}`;
            if (!groups.has(toKey)) {
                groups.set(toKey, {
                    coordinate: { lat: path.To.Latitude, lng: path.To.Longitude },
                    paths: []
                });
            }
            groups.get(toKey).paths.push({ ...path, index: idx, endpoint: 'to' });
        });

        return groups;
    }, [props.flightPaths, roundCoord]);

    const groupedLocationsRef = useRef(groupedLocations);
    useEffect(() => { groupedLocationsRef.current = groupedLocations; }, [groupedLocations]);

    const handleMarkerClick = useCallback((location, index) => {
        if (location.ListingID && listingByIdRef.current.size === 0) {
            return;
        }

        if (props.flightPaths && props.flightPaths.length > 0) {
            const key = `${roundCoord(location.Latitude)},${roundCoord(location.Longitude)}`;
            const flightGroup = groupedFlightPaths.get(key);
            if (flightGroup && flightGroup.paths.length > 0) {
                if (flightGroup.paths.length > 1) {
                    setFlightWindow({ path: { position: flightGroup.coordinate }, pathIndex: null, mode: 'list', pathsAtPoint: flightGroup.paths, filter: 'departure' });
                } else {
                    const path = flightGroup.paths[0];
                    setFlightWindow({ path: { ...path, position: flightGroup.coordinate }, pathIndex: path.index, mode: 'detail', pathsAtPoint: [path], filter: 'departure' });
                }
                setListingWindow(prev => ({ ...prev, location: null }));
                return;
            }
        }

        const key = `${roundCoord(location.Latitude)},${roundCoord(location.Longitude)}`;
        const group = groupedLocationsRef.current.get(key);

        if (group && group.locations.length > 1 && listingByIdRef.current.size > 0) {
            setListingWindow({ location: { ...location, index }, mode: 'list', locationsAtPoint: group.locations });
            setFlightWindow(prev => ({ ...prev, path: null }));
            return;
        }

        let locationWithData = { ...location, index };

        if (location.ListingID && listingByIdRef.current.size > 0) {
            const listing = listingByIdRef.current.get(Number(location.ListingID));
            if (listing) {
                locationWithData.ListingData = {
                    DisplayTitle: listing.DisplayTitle || listing.ListingTitle,
                    Price: listing.RetailPrice,
                    PriceLabel: listing.PriceLabel,
                    Location: listing.DealerLocation,
                    ListingLocation: listing.ListingLocation,
                    DealerName: listing.Dealer,
                    DealerPhone: listing.RetailPhoneNumber,
                    DealerContact: listing.DealerContact,
                    DealerCRMID: listing.DealerCRMID,
                    DealerGroupID: listing.DealerGroupID,
                    ListingID: listing.Id,
                    ListingURL: listing.DetailUrl,
                    LinkLabel: listing.DetailUrlText,
                    FormattedPhone: listing.FormattedPhoneNumber
                };
            }
        }

        setListingWindow({ location: locationWithData, mode: 'detail', locationsAtPoint: [] });
        setFlightWindow(prev => ({ ...prev, path: null }));
    }, [props.flightPaths, roundCoord]);

    const handleInfoWindowClose = useCallback(() => {
        setListingWindow({ location: null, mode: 'detail', locationsAtPoint: [] });
    }, []);

    const handleFlightPathInfoWindowClose = useCallback(() => {
        setFlightWindow({ path: null, pathIndex: null, mode: 'detail', pathsAtPoint: [], filter: 'departure' });
    }, []);

    const closeAllInfoWindows = useCallback(() => {
        setListingWindow({ location: null, mode: 'detail', locationsAtPoint: [] });
        setFlightWindow({ path: null, pathIndex: null, mode: 'detail', pathsAtPoint: [], filter: 'departure' });
    }, []);

    const closeAllInfoWindowsRef = useRef(closeAllInfoWindows);
    useEffect(() => {
        closeAllInfoWindowsRef.current = closeAllInfoWindows;
    }, [closeAllInfoWindows]);

    useEffect(() => {
        if (!map) return;
        const clickListener = map.addListener('click', () => closeAllInfoWindowsRef.current());
        const dragListener = map.addListener('dragstart', () => closeAllInfoWindowsRef.current());
        return () => {
            google.maps.event.removeListener(clickListener);
            google.maps.event.removeListener(dragListener);
        };
    }, [map]);

    const showListingDetail = useCallback((listingId) => {
        const listing = listingByIdRef.current.get(Number(listingId));
        if (!listing) return;

        setListingWindow(prev => {
            const location = prev.locationsAtPoint.find(loc => Number(loc.ListingID) === Number(listingId));
            if (!location) return prev;
            return {
                ...prev,
                mode: 'detail',
                location: {
                    ...location,
                    ListingData: {
                        DisplayTitle: listing.DisplayTitle || listing.ListingTitle,
                        Price: listing.RetailPrice,
                        PriceLabel: listing.PriceLabel,
                        Location: listing.DealerLocation,
                        ListingLocation: listing.ListingLocation,
                        DealerName: listing.Dealer,
                        DealerPhone: listing.RetailPhoneNumber,
                        DealerContact: listing.DealerContact,
                        DealerCRMID: listing.DealerCRMID,
                        DealerGroupID: listing.DealerGroupID,
                        ListingID: listing.Id,
                        ListingURL: listing.DetailUrl,
                        LinkLabel: listing.DetailUrlText,
                        FormattedPhone: listing.FormattedPhoneNumber
                    }
                }
            };
        });
    }, []);

    const handleBackToList = useCallback(() => {
        setListingWindow(prev => ({ ...prev, mode: 'list' }));
    }, []);

    const showFlightPathDetail = useCallback((pathIndex) => {
        setFlightWindow(prev => {
            const path = prev.pathsAtPoint.find(p => p.index === pathIndex);
            if (!path) return prev;
            const position = path.endpoint === 'to'
                ? { lat: path.To.Latitude, lng: path.To.Longitude }
                : { lat: path.From.Latitude, lng: path.From.Longitude };
            return { ...prev, path: { ...path, position }, pathIndex, mode: 'detail' };
        });
    }, []);

    const handleBackToFlightPathList = useCallback(() => {
        setFlightWindow(prev => ({ ...prev, mode: 'list' }));
    }, []);

    const filteredFlightPaths = useMemo(() => {
        const { pathsAtPoint, filter } = flightWindow;
        if (!pathsAtPoint.length) return [];
        return pathsAtPoint.filter(p => filter === 'departure' ? p.endpoint === 'from' : p.endpoint === 'to');
    }, [flightWindow]);

    const flightCounts = useMemo(() => ({
        departures: flightWindow.pathsAtPoint.filter(p => p.endpoint === 'from').length,
        arrivals: flightWindow.pathsAtPoint.filter(p => p.endpoint === 'to').length,
    }), [flightWindow]);

    useEffect(() => {
        if (!flightWindow.pathsAtPoint.length) return;
        if (flightWindow.filter === 'departure' && flightCounts.departures === 0 && flightCounts.arrivals > 0) {
            setFlightWindow(prev => ({ ...prev, filter: 'arrival' }));
        } else if (flightWindow.filter === 'arrival' && flightCounts.arrivals === 0 && flightCounts.departures > 0) {
            setFlightWindow(prev => ({ ...prev, filter: 'departure' }));
        }
    }, [flightWindow.pathsAtPoint, flightWindow.filter, flightCounts]);

    useEffect(() => {
        if (flightListScrollRef.current) {
            flightListScrollRef.current.scrollTop = 0;
        }
    }, [flightWindow.filter]);

    useEffect(() => {
        if (!map || !flightWindow.path?.position || flightWindow.mode !== 'list') return;
        suppressNextIdleSearchRef.current = true;
        map.panTo(flightWindow.path.position);
        //center info window
        map.panBy(0, -130);
    }, [flightWindow.filter, map]);

    const handleMarkerClickRef = useRef(handleMarkerClick);
    useEffect(() => {
        handleMarkerClickRef.current = handleMarkerClick;
    }, [handleMarkerClick]);

    const markersByKeyRef = useRef(new Map());
    const prevLocationsRef = useRef(null);

    useEffect(() => {
        if (!map || prevLocationsRef.current === props.locations) return;
        prevLocationsRef.current = props.locations;

        const existingKeys = markersByKeyRef.current;
        const newGroups = groupedLocations;

        const removeMarker = (m) => m.setMap(null);

        existingKeys.forEach((marker, key) => {
            if (!newGroups.has(key)) {
                removeMarker(marker);
                existingKeys.delete(key);
            }
        });

        newGroups.forEach((group, key) => {
            if (!existingKeys.has(key)) {
                const location = group.locations[0];
                const count = group.locations.length;
                const listingTitle = count === 1 ? (() => { const l = listingByIdRef.current.get(Number(location.ListingID)); return location.DisplayText || l?.DisplayTitle || l?.ListingTitle || location.Label || null; })() : null;
                const title = count > 1 ? `${count} listings` : (listingTitle || `Location ${location.index + 1}`);

                const marker = new google.maps.Marker({ position: group.coordinate, map, icon: MARKER_ICON, title });
                marker.addListener('click', () => handleMarkerClickRef.current(location, location.index));
                existingKeys.set(key, marker);
            }
        });
    }, [props.locations, map, MARKER_ICON, groupedLocations]);

    useEffect(() => {
        return () => {
            markersByKeyRef.current.forEach(m => m.setMap(null));
            markersByKeyRef.current.clear();
        };
    }, []);

    const polylineRefs = useRef([]);

    const flightWindowRef = useRef(flightWindow);
    useEffect(() => {
        flightWindowRef.current = flightWindow;
    }, [flightWindow]);

    useEffect(() => {
        if (!props.flightPaths || !map) return;

        const arrowSymbol = {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            strokeColor: '#0066b3',
        };

        const polylineConfig = {
            icons: [{
                icon: arrowSymbol,
                scale: 5,
                strokeColor: "#0066b3",
                offset: '50%'
            }],
            geodesic: true,
            strokeColor: "#0066b3",
            strokeOpacity: 1.0,
            strokeWeight: 2
        };

        const newFlightPaths = props.flightPaths.map((path, index) => {
            const flightPath = new google.maps.Polyline({
                ...polylineConfig,
                path: [
                    { lat: path.From.Latitude, lng: path.From.Longitude },
                    { lat: path.To.Latitude, lng: path.To.Longitude }
                ]
            });

            flightPath.addListener('click', () => {
                const newIndex = flightWindowRef.current.pathIndex === index ? null : index;
                setFlightWindow(prev => ({
                    ...prev,
                    pathIndex: newIndex,
                    path: newIndex === null ? null : { ...path, index, position: { lat: path.From.Latitude, lng: path.From.Longitude } }
                }));
            });

            flightPath.setMap(map);
            return flightPath;
        });

        polylineRefs.current = newFlightPaths;

        return () => {
            newFlightPaths.forEach(fp => fp.setMap(null));
            polylineRefs.current = [];
        };
    }, [props.flightPaths, map]);

    return (
        <>
            {listingWindow.location && (
                <InfoWindow
                    position={{
                        lat: listingWindow.location.Latitude,
                        lng: listingWindow.location.Longitude
                    }}
                    onClose={handleInfoWindowClose}
                >
                    {listingWindow.mode === 'list' ? (
                        <div className='bm-list-container'>
                            <h3 className='bm-iw-header bm-iw-header--list'>
                                {listingWindow.locationsAtPoint.length} {listingByIdRef.current.get(Number(listingWindow.locationsAtPoint[0]?.ListingID))?.GroupLabel || 'Listings'}
                            </h3>
                            <div className='bm-list-scroll'>
                                {listingWindow.locationsAtPoint.map((loc) => {
                                    const listing = listingByIdRef.current.get(Number(loc.ListingID));
                                    if (!listing) return null;

                                    const cardTitle = listing.DisplayTitle || listing.ListingTitle;

                                    return (
                                        <div key={loc.ListingID} onClick={() => showListingDetail(loc.ListingID)} className='bm-list-card'>
                                            <div className='bm-list-card-title'>{cardTitle}</div>
                                            {listing.Dealer && <div className='bm-list-card-sub'>{listing.Dealer}</div>}
                                            {formatPrice(listing.RetailPrice) && <div className='bm-list-card-price'>{listing.RetailPrice}</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className='bm-iw'>
                            <h3 className='bm-iw-header bm-iw-header--tight'>
                                {listingWindow.location.ListingData?.DisplayTitle || listingWindow.location.DisplayText || listingWindow.location.Label || listingWindow.location.Name || `Location ${listingWindow.location.index + 1}`}
                            </h3>
                            <div className='bm-iw-body'>
                                {listingWindow.location.ListingData && (
                                    <>
                                        {formatPrice(listingWindow.location.ListingData.Price) && (
                                            <div className='bm-iw-field bm-iw-field--price'>
                                                <strong>{listingWindow.location.ListingData.PriceLabel || 'Price'}:</strong> {listingWindow.location.ListingData.Price}
                                            </div>
                                        )}
                                        {(listingWindow.location.ListingData.ListingLocation || listingWindow.location.ListingData.Location) && (
                                            <div className='bm-iw-field'>
                                                <strong>{listingWindow.location.ListingData.ListingLocation ? 'Aircraft Location' : 'Location'}:</strong> {listingWindow.location.ListingData.ListingLocation || listingWindow.location.ListingData.Location}
                                            </div>
                                        )}
                                        {(listingWindow.location.ListingData.DealerName || listingWindow.location.ListingData.DealerPhone) && (
                                            <div className='bm-iw-dealer'>
                                                {listingWindow.location.ListingData.DealerName && (
                                                    <>
                                                        <div className='bm-iw-label bm-iw-label--charter'>CHARTER CONTACT</div>
                                                        <div className='bm-iw-value'>{listingWindow.location.ListingData.DealerName}</div>
                                                    </>
                                                )}
                                                {listingWindow.location.ListingData.DealerPhone && (
                                                    <div className='bm-iw-field'>
                                                        <strong>Phone:</strong>{' '}
                                                        <a href={`tel:${listingWindow.location.ListingData.DealerPhone}`} style={{ color: '#333', textDecoration: 'none' }}>
                                                            <i className='fas fa-phone-alt fa-flip-horizontal' style={{ fontSize: '13px', marginRight: '4px' }} />
                                                            {listingWindow.location.ListingData.FormattedPhone || listingWindow.location.ListingData.DealerPhone}
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {listingWindow.location.ListingData.ListingURL && (
                                            <div className='bm-action-btn-wrap'>
                                                <a href={listingWindow.location.ListingData.ListingURL} className='bm-action-btn'>
                                                    {listingWindow.location.ListingData.LinkLabel || 'View Details'}
                                                </a>
                                            </div>
                                        )}
                                        {listingWindow.locationsAtPoint.length > 1 && (
                                            <div className='bm-back-btn-wrap'>
                                                <button onClick={handleBackToList} className='bm-back-btn'>
                                                    ⬅ Back to List
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}

                                {!listingWindow.location.ListingData && (
                                    <>
                                        {listingWindow.location.AirportCode && (
                                            <div className='bm-iw-field'>
                                                <strong>Airport:</strong> {listingWindow.location.AirportCode}
                                            </div>
                                        )}
                                        {listingWindow.location.City && (
                                            <div className='bm-iw-field'>
                                                <strong>City:</strong> {listingWindow.location.City}
                                                {listingWindow.location.State && `, ${listingWindow.location.State}`}
                                                {listingWindow.location.Country && ` (${listingWindow.location.Country})`}
                                            </div>
                                        )}
                                        {listingWindow.location.Address && (
                                            <div className='bm-iw-field'>
                                                <strong>Address:</strong> {listingWindow.location.Address}
                                            </div>
                                        )}
                                        <div className='bm-iw-coord'>
                                            <div className='bm-iw-coord-row'>
                                                <strong>Lat:</strong> {listingWindow.location.Latitude.toFixed(6)}
                                            </div>
                                            <div>
                                                <strong>Lng:</strong> {listingWindow.location.Longitude.toFixed(6)}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </InfoWindow>
            )}

            {flightWindow.path && (
                <InfoWindow
                    position={flightWindow.path.position}
                    onClose={handleFlightPathInfoWindowClose}
                >
                    {flightWindow.mode === 'list' ? (
                        <div className='bm-list-container'>
                            {(flightCounts.departures > 0 || flightCounts.arrivals > 0) && (
                                <div className='bm-tab-bar'>
                                    {flightCounts.departures > 0 && (
                                        <button
                                            onClick={() => setFlightWindow(prev => ({ ...prev, filter: 'departure' }))}
                                            className={`bm-tab-btn${flightWindow.filter === 'departure' ? ' bm-tab-btn--active' : ''}`}
                                        >
                                            Departure ({flightCounts.departures})
                                        </button>
                                    )}
                                    {flightCounts.arrivals > 0 && (
                                        <button
                                            onClick={() => setFlightWindow(prev => ({ ...prev, filter: 'arrival' }))}
                                            className={`bm-tab-btn${flightWindow.filter === 'arrival' ? ' bm-tab-btn--active' : ''}`}
                                        >
                                            Arrival ({flightCounts.arrivals})
                                        </button>
                                    )}
                                </div>
                            )}
                            <div ref={flightListScrollRef} className='bm-flight-list-scroll'>
                                {filteredFlightPaths.length > 0 ? (
                                    filteredFlightPaths.map((path) => (
                                        <div key={path.index} onClick={() => showFlightPathDetail(path.index)} className='bm-list-card'>
                                            <div className='bm-list-card-title'>
                                                {path.endpoint === 'to'
                                                    ? <>{path.ToLocation} <span className='bm-flight-arrow'>←</span> {path.DepartureLocation}</>
                                                    : <>{path.DepartureLocation} <span className='bm-flight-arrow'>→</span> {path.ToLocation}</>
                                                }
                                            </div>
                                            {path.AircraftDescription && (
                                                <div className='bm-list-card-sub'>{path.AircraftDescription}</div>
                                            )}
                                            {path.DepartureDateTime && (
                                                <div className='bm-list-card-date'>
                                                    {new Date(path.DepartureDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className='bm-list-empty'>
                                        No {flightWindow.filter} flights at this location
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className='bm-flight-container'>
                            <div className='bm-flight-route'>
                                <div className='bm-flight-route-text'>
                                    {flightWindow.path.DepartureLocation} <span className='bm-flight-arrow'>→</span> {flightWindow.path.ToLocation}
                                </div>
                            </div>
                            <div className='bm-flight-details'>
                                {flightWindow.path.AircraftDescription && (
                                    <div>
                                        <div className='bm-iw-label'>Aircraft</div>
                                        <div className='bm-iw-value'>{flightWindow.path.AircraftDescription}</div>
                                    </div>
                                )}
                                {flightWindow.path.DepartureDateTime && (
                                    <div>
                                        <div className='bm-iw-label'>Departure</div>
                                        <div className='bm-iw-value'>
                                            {new Date(flightWindow.path.DepartureDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                    </div>
                                )}
                                {flightWindow.path.AccountName && (
                                    <div>
                                        <div className='bm-iw-label'>Company</div>
                                        <div className='bm-iw-value'>{flightWindow.path.AccountName}</div>
                                    </div>
                                )}
                                {flightWindow.path.AccountPhone && (
                                    <div>
                                        <div className='bm-iw-label'>Phone</div>
                                        <a href={`tel:${flightWindow.path.AccountPhone}`} style={{ fontSize: '14px', color: '#0066b3', marginTop: '1px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500' }}>
                                            <i className="fa-solid fa-phone" style={{ fontSize: '14px', color: '#000' }}></i>
                                            {flightWindow.path.AccountPhone}
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className='bm-flight-actions'>
                                {props.onRequestQuote && (
                                    <button onClick={() => props.onRequestQuote(flightWindow.path)} className='bm-action-btn'>
                                        Request a Quote
                                    </button>
                                )}
                                {flightWindow.pathsAtPoint.length > 1 && (
                                    <button onClick={handleBackToFlightPathList} className='bm-back-btn'>
                                        ⬅ Back to List
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </InfoWindow>
            )}
        </>
    );
}));

const BOUNDS_BUFFER_MULTIPLIER = 0.15;

const expandBounds = (bounds) => {
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;
    const latPad = latSpan * BOUNDS_BUFFER_MULTIPLIER;
    const lngPad = lngSpan * BOUNDS_BUFFER_MULTIPLIER;
    return {
        south: bounds.south - latPad,
        north: bounds.north + latPad,
        west: bounds.west - lngPad,
        east: bounds.east + lngPad
    };
};

const BaseMap = memo(forwardRef((props, ref) => {
    const baseMapContentRef = useRef(null);
    const isSearchDrivenUpdate = useRef(false);

    useImperativeHandle(ref, () => ({
        setMapCenter: (...args) => {
            isSearchDrivenUpdate.current = true;
            baseMapContentRef.current?.setMapCenter(...args);
        }
    }));

    const defaultMapStyles = useMemo(() => [
        {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
        }
    ], []);

    return (
        <GoogleMap
            defaultCenter={props.defaultCenter ? { lat: props.defaultCenter.Latitude, lng: props.defaultCenter.Longitude } : { lat: 20, lng: 0 }}
            defaultZoom={props.defaultZoom ? props.defaultZoom : 2}
            mapId={props.mapId}
            gestureHandling="greedy"
            disableDefaultUI={false}
            zoomControl={true}
            mapTypeControl={false}
            scaleControl={false}
            streetViewControl={false}
            rotateControl={false}
            fullscreenControl={true}
            styles={props.mapStyles || defaultMapStyles}
            className={props.className}
        >
            <BaseMapContent
                ref={baseMapContentRef}
                locations={props.locations}
                listingMapData={props.listingMapData}
                flightPaths={props.flightPaths}
                currentUser={props.currentUser}
                defaultCenter={props.defaultCenter}
                disableAutoCentering={props.disableAutoCentering}
                enableGeolocation={props.enableGeolocation}
                onLocationChange={props.onLocationChange}
                onRequestQuote={props.onRequestQuote}
                onEmailSeller={props.onEmailSeller}
                isSearchDrivenUpdate={isSearchDrivenUpdate}
            />
        </GoogleMap>
    );
}));

export default BaseMap;
